import { createAction, handleActions } from "redux-actions";
import { takeLatest } from 'redux-saga/effects';
import createRequestSaga, { createRequestActionTypes } from "./lib/createRequestSaga";
import * as blackListAPI from "../api/blackList";
import produce from "immer";
import { blackSuccessProps, CommonProps, GetPayload } from "../types/commons";
import { blackIdProps, BlackState, PostBlackProps } from "../types/blackList";

/* 블랙 리스트 객실 등록, 수정, 상제 부분 */
const INITIALIZE = 'blackList/INITIALIZE';
const CHANGE_FINELD = 'blackList/CHANGE_FINELD'; //데이터 변경용
const CHANGE_RESULT = 'blackList/CHANGE_RESULT'; //결과값 변경

//리덕스 모듈에서 API를 사용할 수 있게 추가
const [BLACKLIST_TYPE, BLACKLIST_TYPE_SUCCESS, BLACKLIST_TYPE_FAILURE] = createRequestActionTypes('blackList/BLACK_LIST_TYPE');
const [BLACKLIST_CREATE, BLACKLIST_CREATE_SUCCESS, BLACKLIST_CREATE_FAILURE] = createRequestActionTypes('blackList/BLACK_LIST_CREATE');
const [BLACKLIST_DELETE, BLACKLIST_DELETE_SUCCESS, BLACKLIST_DELETE_FAILURE] = createRequestActionTypes('blackList/BLACK_LIST_DELETE');
const [BLACKLIST_UPDATE, BLACKLIST_UPDATE_SUCCESS, BLACKLIST_UPDATE_FAILURE] = createRequestActionTypes('blackList/BLACK_LIST_UPDATE');
const [BLACKLIST_SELECT, BLACKLIST_SELECT_SUCCESS, BLACKLIST_SELECT_FAILURE] = createRequestActionTypes('blackList/BLACK_LIST_SELECT');

/* action */
export const initialize = createAction(INITIALIZE);
export const changeField = createAction(
  CHANGE_FINELD,
  ({ key, value }:CommonProps) => ({
    key, // blackList 내부 > name, blackId 등
    value, // 실제 바꾸려는 값
  }),
);

export const changeResult = createAction(
  CHANGE_RESULT,
  ({ key, value }:CommonProps) => ({
    key, // blackList success, error 변경
    value, // 실제 바꾸려는 값
  }),
);

// 블랙리스트 객실 타입 목록
export const listBlackAction = createAction(BLACKLIST_TYPE);

// 블랙리스트 객실 조회 정보
export const selectBlackListAction = createAction(BLACKLIST_SELECT, ({ id }:blackIdProps) => ({
  id
}));

// 블랙리스트 객실 등록 시도
export const createBlackListAction = createAction(BLACKLIST_CREATE, ({ hotel_name, room_no, room_type_code  }:PostBlackProps) => ({
  hotel_name, room_no, room_type_code
}));

// 블랙리스트 객실 수정 시도
export const updateBlackListAction = createAction(BLACKLIST_UPDATE, ({ id, room_no, room_type_code }:PostBlackProps) => ({
  id,room_no, room_type_code
}));

// 블랙리스트 객실 삭제 시도
export const deleteBlackListAction = createAction(BLACKLIST_DELETE, ({ id }:blackIdProps) => ({
  id
}));

// 사가 생성
const listBlackTypeSaga = createRequestSaga(BLACKLIST_TYPE, blackListAPI.listBlackList);
const createBlackListSaga = createRequestSaga(BLACKLIST_CREATE, blackListAPI.createBlackList);
const deleteBlackListSaga = createRequestSaga(BLACKLIST_DELETE, blackListAPI.deleteBlackList);
const updateBlackListSaga = createRequestSaga(BLACKLIST_UPDATE, blackListAPI.updateBlackList);
const selectBlackListSaga = createRequestSaga(BLACKLIST_SELECT, blackListAPI.selectBlackList);

export function* blackListSaga(){
  yield takeLatest(BLACKLIST_TYPE, listBlackTypeSaga);
  yield takeLatest(BLACKLIST_CREATE, createBlackListSaga);
  yield takeLatest(BLACKLIST_DELETE, deleteBlackListSaga);
  yield takeLatest(BLACKLIST_UPDATE, updateBlackListSaga);
  yield takeLatest(BLACKLIST_SELECT, selectBlackListSaga);
}

//initialState
const initialState:BlackState = {
  blackList:{
    id: '',
    hotel_id: '',
    hotel_name: '',
    room_no: '',
    room_type_code: '',
    createdAt: null,
    updatedAt: null,
  },    
  blackCreateSuccess: false, //등록 성공 관련
  blackUpdateSuccess: false, //수정 성공 관련
  blackDeleteSuccess: false, //삭제 성공 관련
  blackCreateError: null,
  blackUpdateError: null,
  blackDeleteError: null,
};

// 페이로드를 가져오기 위한 헬퍼 타입
type Payloads = GetPayload<typeof initialize | typeof changeField | typeof changeResult | typeof listBlackAction
| typeof selectBlackListAction | typeof createBlackListAction | typeof updateBlackListAction | typeof deleteBlackListAction>;

const blackList = handleActions<BlackState, Payloads>(
  {
    [INITIALIZE] : state => initialState, // initialState를 넣으면 초기 상태로 바뀜
    [CHANGE_FINELD] : (state, { payload: {key, value} }) => 
    produce(state, draft => {
      if(key === 'hotel_name') draft.blackList.hotel_name = value;
      if(key === 'id') draft.blackList.id = value;
      if (key === 'room_no') draft.blackList.room_no = value as string;
      if (key === 'room_type_code') draft.blackList.room_type_code = value as string;
    }),
    [CHANGE_RESULT] : (state, { payload: {key, value} }:blackSuccessProps) => 
    produce(state, draft => {
      // @ts-ignore: key가 string으로 들어와서 타입 추론이 어려울 때 예외 처리
      draft[key] = value;
    }),
    // 블랙리스트 객실 조회 성공
    [BLACKLIST_TYPE_SUCCESS] : (state) => ({
      ...state,
      blackCreateError: null,
    }),
    // 블랙리스트 객실 조회 실패
    [BLACKLIST_TYPE_FAILURE] : (state, { payload: error }) => ({
      ...state,
      blackCreateError: error,
    }),
    // 블랙리스트 객실 등록 화면 조회 성공
    [BLACKLIST_CREATE_SUCCESS] : (state, { payload: blackList }) => ({
      ...state,
      blackList: blackList,
      blackCreateSuccess: true,
      blackCreateError: null,
    }),
    // 블랙리스트 객실 등록 화면 조회 실패
    [BLACKLIST_CREATE_FAILURE] : (state, { payload: error }) => ({
      ...state,
      blackCreateError: error,
    }),
    // 블랙리스트 객실 삭제 성공
    [BLACKLIST_DELETE_SUCCESS] : (state, { payload: blackList }) => ({
      ...state,
      blackDeleteSuccess: true,
      blackDeleteError: null,
    }),
    // 블랙리스트 객실 삭제 실패
    [BLACKLIST_DELETE_FAILURE] : (state, { payload: error }) => ({
      ...state,
      blackDeleteError: error,
    }),
    // 블랙리스트 객실 수정 성공
    [BLACKLIST_UPDATE_SUCCESS] : (state, { payload: blackList }) => ({
      ...state,
      blackUpdateSuccess: true,
      blackUpdateError: null,
    }),
      // 블랙리스트 객실 수정 실패
    [BLACKLIST_UPDATE_FAILURE] : (state, { payload: error }) => ({
      ...state,
      blackUpdateError: error,
    }),         
    // 블랙리스트 객실 검색 조회 성공
    [BLACKLIST_SELECT_SUCCESS] : (state, { payload: blackListselect }) => ({
      ...state,
      blackList:{
        id: blackListselect.id,
        hotel_id: blackListselect.hotel_id,
        hotel_name: blackListselect.hotel_name,
        room_type_code: blackListselect.room_type_code,
        room_no: blackListselect.room_no,
        createdAt: blackListselect.created_at,
        updatedAt: blackListselect.updated_at,
      }
    }),
    // 블랙리스트 객실 검색 조회 실패
    [BLACKLIST_SELECT_FAILURE] : (state, { payload: error }) => ({
      ...state,
      blackUpdateError: error,
    }),
  },
  initialState,
);

export default blackList;
